/**
 * AppLayout - Shared application layout component
 *
 * Renders the header, navbar, and sidebar structure that's shared across all pages
 * Views mount their content into the `.main-content` area
 */

import { SidebarLoader } from '../ui/SidebarLoader';
import { FavoritesManager } from './FavoritesManager';
import type { StorageService } from '../services/StorageService';
import type { Route } from '@/types';

export class AppLayout {
	/**
	 * Sidebar loader instance
	 */
	private sidebarLoader: SidebarLoader;

	/**
	 * Favorites manager instance
	 */
	private favoritesManager: FavoritesManager | null = null;

	/**
	 * Storage service instance
	 */
	private storageService: StorageService;

	/**
	 * Routes for favorites
	 */
	private routes: Route[];

	/**
	 * Constructor
	 */
	constructor(sidebarLoader: SidebarLoader, storageService: StorageService, routes: Route[]) {
		this.sidebarLoader = sidebarLoader;
		this.storageService = storageService;
		this.routes = routes;
	}

	/**
	 * Initialize the layout
	 * Renders the layout HTML and loads the sidebar
	 */
	async initialize(container: HTMLElement): Promise<void> {
		// Render layout HTML
		container.innerHTML = this.renderLayout();

		// Load sidebar
		await this.sidebarLoader.loadSidebar();

		// Initialize Lucide icons
		if (typeof window.lucide !== 'undefined') {
			window.lucide.createIcons();
		}

		// Initialize favorites manager
		this.favoritesManager = new FavoritesManager(this.storageService, this.routes);
		this.favoritesManager.initialize();

		// Set up search functionality
		this.setupSearch();
	}

	/**
	 * Render the complete layout HTML
	 */
	private renderLayout(): string {
		return `
			${this.renderHeader()}
			${this.renderNavbar()}
			<div class="content-container">
				<div class="sidebar" id="sidebar-container"></div>
				<div class="main-content" id="view-container">
					<!-- Views will be mounted here -->
				</div>
			</div>
		`;
	}

	/**
	 * Render header section
	 */
	private renderHeader(): string {
		return `
			<div class="header">
				<div class="header-left">
					<div class="logo"><a href="/">Z&P<br>Zapital IQ</a></div>
					<div class="search-container">
						<div class="search-wrapper">
							<div class="search-box-wrapper">
								<input
									type="text"
									class="search-box"
									id="search-input"
									placeholder="Search Companies, People, Funds, and More..."
								>
							</div>
							<div class="search-dropdown" id="search-dropdown">
								<div class="search-option" data-route="/profile">
									<i
										data-lucide="building"
										style="width: 16px; height: 16px; color: #666; display: inline-block; vertical-align: middle;"
									></i>
									<span>NVIDIA Corporation (NasdaqGS:NVDA)</span>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div class="top-links">
					<a href="#" id="favorites-link">
						Favorites <span class="dropdown-arrow">▼</span>
					</a> |
					<a href="#" onclick="window.print(); return false;">Print</a> |
					<a href="/">Logout</a>
				</div>
			</div>
		`;
	}

	/**
	 * Render navbar section
	 */
	private renderNavbar(): string {
		return `
			<div class="navbar">
				<div class="navbar-left">
					<a href="#" class="navbar-selected">Companies</a>
				</div>
			</div>
		`;
	}

	/**
	 * Set up search functionality
	 */
	private setupSearch(): void {
		const searchInput = document.getElementById('search-input') as HTMLInputElement;
		const searchDropdown = document.getElementById('search-dropdown');
		const searchOption = document.querySelector('.search-option') as HTMLElement;

		if (!searchInput || !searchDropdown || !searchOption) return;

		// Show dropdown when user types
		searchInput.addEventListener('input', () => {
			const value = searchInput.value.toLowerCase().trim();
			if (value.length > 0) {
				// Show dropdown if input matches NVIDIA
				const nvidiaText = 'NVIDIA Corporation (NasdaqGS:NVDA)'.toLowerCase();
				if (nvidiaText.includes(value)) {
					searchDropdown.style.display = 'block';
				} else {
					searchDropdown.style.display = 'none';
				}
			} else {
				searchDropdown.style.display = 'none';
			}
		});

		// Show dropdown on focus
		searchInput.addEventListener('focus', () => {
			if (searchInput.value.trim().length > 0) {
				searchDropdown.style.display = 'block';
			}
		});

		// Navigate to profile when option is clicked
		searchOption.addEventListener('click', () => {
			const route = searchOption.getAttribute('data-route');
			if (route) {
				window.location.href = route;
			}
		});

		// Handle Enter key
		searchInput.addEventListener('keydown', (e) => {
			if (e.key === 'Enter') {
				const value = searchInput.value.toLowerCase().trim();
				if (value.length > 0) {
					const nvidiaText = 'NVIDIA Corporation (NasdaqGS:NVDA)'.toLowerCase();
					if (nvidiaText.includes(value)) {
						window.location.href = '/profile';
					}
				}
			}
		});

		// Hide dropdown when clicking outside
		document.addEventListener('click', (e) => {
			const target = e.target as HTMLElement;
			if (!searchInput.contains(target) && !searchDropdown.contains(target)) {
				searchDropdown.style.display = 'none';
			}
		});
	}

	/**
	 * Get the view container element
	 */
	getViewContainer(): HTMLElement | null {
		return document.getElementById('view-container');
	}

	/**
	 * Update the active sidebar item
	 */
	updateActiveSidebar(itemName: string): void {
		document.body.setAttribute('data-active-sidebar', itemName);
		this.sidebarLoader.highlightActiveItem(itemName);
	}
}
